<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useStore } from 'vuex';
import Checkbox from 'primevue/checkbox';
import InputNumber from 'primevue/inputnumber';
import Panel from 'primevue/panel';
import { getCookie } from '@/utils/cookie';
import { useShipmentFormLearnedDedupSettingsStore } from '@/stores/useShipmentFormLearnedDedupSettingsStore';

const store = useStore();
const learnedRoutesAvailable = computed(
  () => store.getters['auth/learnedRoutesAvailable'],
);
const visible = computed(
  () =>
    getCookie('shipment-extended-tool') === '1' &&
    learnedRoutesAvailable.value,
);
const settings = useShipmentFormLearnedDedupSettingsStore();
const { dedupEnabled, matchPercent, maxObservedMatchPercent } =
  storeToRefs(settings);
const maxObservedMatchLabel = computed(() =>
  maxObservedMatchPercent.value == null
    ? 'Not calculated'
    : `${maxObservedMatchPercent.value}%`,
);

onMounted(() => {
  void settings.hydrateFromBrowserDb();
});
</script>

<template>
  <Panel
    v-if="visible"
    header="Learned route dedup (debug)"
    toggleable
    :collapsed="true"
    class="mt-4 border border-gray-200 bg-white shadow-none"
  >
    <div class="space-y-4 text-sm">
      <p
        class="rounded-md border border-gpx_gray-800 bg-gpx_gray-900 px-3 py-2 text-xs leading-relaxed text-gpx_gray-100"
      >
        Hide a learned route when its buffered geometry overlaps a Google
        alternative by at least the selected percentage. Higher percentages
        require a closer match before the learned route is hidden.
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <Checkbox
          input-id="shipment-learned-dedup-enabled"
          binary
          :model-value="dedupEnabled"
          @update:model-value="settings.setDedupEnabled(Boolean($event))"
        />
        <label
          for="shipment-learned-dedup-enabled"
          class="cursor-pointer font-medium text-gpx_black-200"
        >
          Deduplicate learned routes against Google
        </label>
      </div>

      <div class="max-w-52">
        <label
          for="shipment-learned-dedup-match-percent"
          class="mb-1 block text-xs font-medium text-gpx_gray-100"
        >
          Minimum geometry match
        </label>
        <InputNumber
          input-id="shipment-learned-dedup-match-percent"
          :model-value="matchPercent"
          :min="0"
          :max="100"
          :max-fraction-digits="0"
          suffix="%"
          show-buttons
          button-layout="horizontal"
          size="small"
          class="w-full"
          input-class="w-full"
          :disabled="!dedupEnabled"
          @update:model-value="settings.setMatchPercent(Number($event))"
        />
      </div>

      <div
        class="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
      >
        <span class="text-xs font-medium text-gpx_gray-100">
          Maximum match with a Google route
        </span>
        <span class="text-sm font-semibold tabular-nums text-gpx_black-200">
          {{ maxObservedMatchLabel }}
        </span>
      </div>
    </div>
  </Panel>
</template>
